import Vue from "vue";
import { Store } from "vuex";
import { Action, Module, Mutation, VuexModule, getModule } from "vuex-module-decorators";

import { isModelStale, processCoreModelResponse } from "@/store/-utils";
import LikesApi from "@/store/likes/-api";

@Module({
  name: "likes",
  stateFactory: true,
  namespaced: true,
})
export default class LikesStore extends VuexModule {
  likes: LikeMap = {};
  meta: LikesMeta | null = null;
  ids: string[] = [];
  likeAdded = false;
  loggedOutFavLtkId = (process.client ? localStorage.getItem("favorite-post") : "") ?? "";

  get getFeedHasMorePages() {
    return () => this.meta === null || Boolean(this.meta.nextUrl);
  }

  get hasFeedBeenFetched() {
    return this.meta !== null && this.ids.length > 0;
  }

  get hasLikeBeenAdded() {
    return this.likeAdded;
  }

  @Action({ rawError: true })
  async fetchLikesByIds({ ltkIds }: { ltkIds: string[] }) {
    if (ltkIds.length === 0) {
      return;
    }
    try {
      const api = new LikesApi(this.store.$axios);
      const response = await api.get({ ltkIds });

      await processCoreModelResponse(this.store, response.data);
    } catch (error) {}
  }

  /**
   * Normally calling 1 mutation for every 1 model would be discouraged, however, the underlying
   * API call has a special debounce and argument aggregation decorator on it. Because of this,
   * multiple rapid calls to the API will be aggregated into a single call, resulting with only
   * the last call returning results, and the earlier ones having a rejected/cancelled promise.
   */
  @Action({ rawError: true })
  async fetchLikeById(ltkId: string) {
    // Skip refreshing recent models
    if (!isModelStale(this.likes[ltkId])) {
      return;
    }

    try {
      const api = new LikesApi(this.store.$axios);
      const response = await api.getOne(ltkId);

      await processCoreModelResponse(this.store, response.data);
    } catch (error) {
      // null is returned from the debounce decorator. All other errors should be considered action failures.
      if (error === null) {
        return undefined;
      }
    }
  }

  @Action({ rawError: true })
  async fetchLikesFeed({ overwrite }: { overwrite: boolean }) {
    const api = new LikesApi(this.store.$axios);
    const meta = this.meta;
    const data: Parameters<(typeof api)["get"]>[0] = {};

    if (!this.getFeedHasMorePages() && overwrite === false) {
      return;
    }

    if (this.ids.length > 0 && this.getFeedHasMorePages()) {
      data.beforeDatetime = this.likes[this.ids[this.ids.length - 1]]?.dateLiked;
      data.limit = meta?.limit;
    }

    const response = await api.get(data);

    // Set core models before feed lists
    await processCoreModelResponse(this.store, response.data);

    const ids = response.data.likes.map(({ ltkId }) => ltkId);
    this.setLikesFeed({ ids, meta: response.data.meta, overwrite });
  }

  @Action({ rawError: true })
  async createLike({ ltkId }: { ltkId: string }) {
    const api = new LikesApi(this.store.$axios);
    const result = await api.post(ltkId);
    const { like } = result.data;

    this.addLike(like);
  }

  @Action({ rawError: true })
  async deleteLike({ ltkId }: { ltkId: string }) {
    // Remove like
    this.removeLike(ltkId);

    try {
      const api = new LikesApi(this.store.$axios);
      await api.deleteOne(ltkId);
    } catch (error) {}
  }

  @Mutation
  addLike(like: Like) {
    Vue.set(this.likes, like.ltkId, like);
    Vue.set(this, "likeAdded", true);
    this.ids.unshift(like.ltkId);
  }

  @Mutation
  setLikes({ likes }: { likes: LikeMap }) {
    Vue.set(this, "likes", Object.assign({}, this.likes, likes));
  }

  @Mutation
  setLikesFeed({ ids, meta, overwrite = false }: { ids: string[]; meta?: LikesMeta; overwrite: boolean }) {
    // Add items to feed
    const start = overwrite ? 0 : this.ids.length;
    const length = overwrite ? this.ids.length : 0;
    this.ids.splice(start, length, ...ids);

    if (meta !== undefined) {
      Vue.set(this, "meta", meta);
    }
  }

  @Mutation
  removeLike(ltkId: string) {
    const idIndex = this.ids.findIndex((id) => id === ltkId);
    if (idIndex !== -1) {
      this.ids.splice(idIndex, 1);
    }

    Vue.delete(this.likes, ltkId);
  }

  @Mutation
  setLoggedOutFavLtkId(ltkId: string) {
    this.loggedOutFavLtkId = ltkId;
  }

  @Mutation
  resetStore() {
    this.meta = null;
    this.ids.splice(0, this.ids.length);
    Vue.set(this, "likes", {});
  }
}

export const getLikesStore = (store: Store<unknown>) => {
  const likesStore = getModule(LikesStore, store);

  return likesStore;
};
